import { invoke } from '@tauri-apps/api/core';
import type { ByteKV, ByteKVBehavior, DocStorage } from '@toeverything/infra';

import { BroadcastChannelDocEventBus } from './doc-broadcast-channel';

async function initWorkspace(workspaceId: string): Promise<void> {
  return invoke('plugin:workspace-storage|init_workspace', { workspaceId });
}

async function docGet(
  workspaceId: string,
  docId: string
): Promise<number[] | null> {
  return invoke('plugin:workspace-storage|doc_get', { workspaceId, docId });
}

async function docSet(
  workspaceId: string,
  docId: string,
  data: number[]
): Promise<void> {
  return invoke('plugin:workspace-storage|doc_set', {
    workspaceId,
    docId,
    data,
  });
}

async function docDelete(workspaceId: string, docId: string): Promise<void> {
  return invoke('plugin:workspace-storage|doc_delete', { workspaceId, docId });
}

async function docKeys(workspaceId: string): Promise<string[]> {
  return invoke('plugin:workspace-storage|doc_keys', { workspaceId });
}

async function kvGet(
  workspaceId: string,
  storeName: string,
  key: string
): Promise<number[] | null> {
  return invoke('plugin:workspace-storage|kv_get', {
    workspaceId,
    storeName,
    key,
  });
}

async function kvSet(
  workspaceId: string,
  storeName: string,
  key: string,
  value: number[]
): Promise<void> {
  return invoke('plugin:workspace-storage|kv_set', {
    workspaceId,
    storeName,
    key,
    value,
  });
}

async function kvDelete(
  workspaceId: string,
  storeName: string,
  key: string
): Promise<void> {
  return invoke('plugin:workspace-storage|kv_delete', {
    workspaceId,
    storeName,
    key,
  });
}

async function kvKeys(
  workspaceId: string,
  storeName: string
): Promise<string[]> {
  return invoke('plugin:workspace-storage|kv_keys', { workspaceId, storeName });
}

export class SqliteDocStorage implements DocStorage {
  constructor(private readonly workspaceId: string) {}

  private initialized = false;

  private async ensureInitialized() {
    if (this.initialized) return;
    await initWorkspace(this.workspaceId);
    this.initialized = true;
  }

  eventBus = new BroadcastChannelDocEventBus(this.workspaceId);
  readonly doc = new SqliteDoc(this.workspaceId, () => this.ensureInitialized());
  readonly syncMetadata = new SqliteKV(
    this.workspaceId,
    'sync-metadata',
    () => this.ensureInitialized()
  );
  readonly serverClock = new SqliteKV(
    this.workspaceId,
    'server-clock',
    () => this.ensureInitialized()
  );
}

type DocType = DocStorage['doc'];

class SqliteDoc implements DocType {
  constructor(
    private readonly workspaceId: string,
    private readonly ensureInitialized: () => Promise<void>
  ) {}

  async get(docId: string): Promise<Uint8Array | null> {
    await this.ensureInitialized();
    const data = await docGet(this.workspaceId, docId);
    if (!data) return null;
    return new Uint8Array(data);
  }

  async set(docId: string, data: Uint8Array): Promise<void> {
    await this.ensureInitialized();
    await docSet(this.workspaceId, docId, Array.from(data));
  }

  async keys(): Promise<string[]> {
    await this.ensureInitialized();
    return docKeys(this.workspaceId);
  }

  async del(key: string): Promise<void> {
    await this.ensureInitialized();
    await docDelete(this.workspaceId, key);
  }

  async clear(): Promise<void> {
    await this.ensureInitialized();
    const allKeys = await docKeys(this.workspaceId);
    for (const key of allKeys) {
      await docDelete(this.workspaceId, key);
    }
  }

  async transaction<T>(
    cb: (transaction: ByteKVBehavior) => Promise<T>
  ): Promise<T> {
    await this.ensureInitialized();
    const wsId = this.workspaceId;

    return await cb({
      get: async (docId: string) => {
        const data = await docGet(wsId, docId);
        if (!data) return null;
        return new Uint8Array(data);
      },
      set: async (docId: string, data: Uint8Array) => {
        await docSet(wsId, docId, Array.from(data));
      },
      keys: async () => {
        return docKeys(wsId);
      },
      del: async (key: string) => {
        await docDelete(wsId, key);
      },
      clear: async () => {
        const allKeys = await docKeys(wsId);
        for (const key of allKeys) {
          await docDelete(wsId, key);
        }
      },
    });
  }
}

class SqliteKV implements ByteKV {
  constructor(
    private readonly workspaceId: string,
    private readonly storeName: string,
    private readonly ensureInitialized: () => Promise<void>
  ) {}

  async get(key: string): Promise<Uint8Array | null> {
    await this.ensureInitialized();
    const data = await kvGet(this.workspaceId, this.storeName, key);
    if (!data) return null;
    return new Uint8Array(data);
  }

  async set(key: string, value: Uint8Array): Promise<void> {
    await this.ensureInitialized();
    await kvSet(this.workspaceId, this.storeName, key, Array.from(value));
  }

  async keys(): Promise<string[]> {
    await this.ensureInitialized();
    return kvKeys(this.workspaceId, this.storeName);
  }

  async del(key: string): Promise<void> {
    await this.ensureInitialized();
    await kvDelete(this.workspaceId, this.storeName, key);
  }

  async clear(): Promise<void> {
    await this.ensureInitialized();
    const allKeys = await kvKeys(this.workspaceId, this.storeName);
    for (const key of allKeys) {
      await kvDelete(this.workspaceId, this.storeName, key);
    }
  }

  async transaction<T>(
    cb: (transaction: ByteKVBehavior) => Promise<T>
  ): Promise<T> {
    await this.ensureInitialized();
    const wsId = this.workspaceId;
    const store = this.storeName;

    return await cb({
      get: async (key: string) => {
        const data = await kvGet(wsId, store, key);
        if (!data) return null;
        return new Uint8Array(data);
      },
      set: async (key: string, value: Uint8Array) => {
        await kvSet(wsId, store, key, Array.from(value));
      },
      keys: async () => {
        return kvKeys(wsId, store);
      },
      del: async (key: string) => {
        await kvDelete(wsId, store, key);
      },
      clear: async () => {
        const allKeys = await kvKeys(wsId, store);
        for (const key of allKeys) {
          await kvDelete(wsId, store, key);
        }
      },
    });
  }
}
